import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, ArrowLeft, Save, Loader, X
} from 'lucide-react';
import adminService, { User as UserType } from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';

const EditUser: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    mobile_number: ''
  });

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUserById(parseInt(userId!));
      setUser(data);
      setFormData({
        full_name: data.full_name,
        email: data.email,
        mobile_number: data.mobile_number || ''
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch user details');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.full_name.trim()) {
      toast.error('Please enter full name');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error('Please enter email');
      return;
    }
    
    try {
      setSaving(true);
      await adminService.updateUserDetails({
        user_id: parseInt(userId!),
        ...formData
      });
      toast.success('User updated successfully!');
      navigate(`/admin/users/${userId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/users/${userId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <p className="text-secondary-600">User not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/admin/users/${userId}`)}
            className="bg-secondary-100 hover:bg-secondary-200 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">Edit User</h1>
            <p className="text-secondary-600">Update user information</p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <form onSubmit={handleSubmit}>
          {/* User Preview Section */}
          <div className="bg-secondary-50 p-8 border-b">
            <div className="flex items-center space-x-6">
              {user.profile_image ? (
                <img
                  src={getImageUrl(user.profile_image)}
                  alt={user.full_name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                />
              ) : (
                <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center border-4 border-white shadow-md">
                  <User className="text-primary-600" size={40} />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-secondary-900">{user.full_name}</h2>
                <p className="text-secondary-600 mt-1">{user.email}</p>
                <p className="text-sm text-secondary-500 mt-1">User ID: {user.user_id}</p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="p-8 space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Full Name <span className="text-danger-500">*</span>
              </label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                  placeholder="Enter full name"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Email Address <span className="text-danger-500">*</span>
              </label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                  placeholder="Enter email address"
                  required
                />
              </div>
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Mobile Number
              </label>
              <div className="relative">
                <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                  className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                  placeholder="Enter mobile number"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 px-8 py-6 bg-secondary-50 border-t">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-3 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-white transition-all disabled:opacity-50"
            >
              <div className="flex items-center space-x-2">
                <X size={20} />
                <span>Cancel</span>
              </div>
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;

